import {
  Account as AccountV2,
  AccountWithRealBalance,
  CashFlow as CashFlowV2,
  CashFlowCollection,
  GroupedCashFlow,
  TransactionCategory,
  TransactionType,
} from '@algoan/dashboard/model';

export const getTotalBalanceV2 = (accounts: AccountV2[]): number =>
  accounts.reduce((total, account) => total + account.balance, 0);

export const getRealTotalBalance = (accounts: AccountWithRealBalance[]): number =>
  accounts.reduce((total, account) => total + account.realBalance, 0);

export const getTotalAmount = (cashFlows: CashFlowV2[]): number =>
  cashFlows.reduce((total, cashFlow) => total + cashFlow.totalAmount, 0);

export const getTotalNumber = (cashFlows: CashFlowV2[]): number =>
  cashFlows.reduce((total, cashFlow) => total + cashFlow.transactions.length, 0);

export const sumTwoCashFlows = (cashFlowA: CashFlowV2, cashFlowB: CashFlowV2): CashFlowV2 => {
  const newCashFlow = { ...cashFlowA };

  newCashFlow.totalAmount += cashFlowB.totalAmount;
  newCashFlow.calendar = cashFlowA.calendar.map((calendar) => ({ ...calendar }));

  if (Array.isArray(cashFlowA.transactions) && Array.isArray(cashFlowB.transactions)) {
    newCashFlow.transactions = [...cashFlowA.transactions, ...cashFlowB.transactions];
  }

  cashFlowB.calendar.forEach((calendar) => {
    const existingCalendar = newCashFlow.calendar.find((existingCalendar) => existingCalendar.month === calendar.month);

    if (typeof existingCalendar !== 'undefined') {
      existingCalendar.amount += calendar.amount;

      return;
    }

    newCashFlow.calendar.push({ ...calendar });
  });

  return newCashFlow;
};

export const getAllFirstAndSixteenthOfTheMonthBetweenTwoDates = (start: Date, end: Date): Date[] => {
  if (start.getTime() > end.getTime()) {
    throw new Error('First date should older than the second one');
  }

  const nextFirstOrSixteenthOfTheMonth = (date: Date): Date => {
    const dayOfMonth = date.getDate();
    const nextDate = new Date(date);
    if (dayOfMonth < 16) {
      nextDate.setDate(16);
    } else {
      nextDate.setMonth(date.getMonth() + 1);
      nextDate.setDate(1);
    }
    return nextDate;
  };

  const searchNextDatesFrom = (date: Date, acc: Date[]): Date[] => {
    const nextDate = nextFirstOrSixteenthOfTheMonth(date);
    if (nextDate.getTime() > end.getTime()) {
      return acc;
    }
    acc.push(nextDate);

    return searchNextDatesFrom(nextDate, acc);
  };

  const startDayOfMonth = start.getDate();
  const initial = startDayOfMonth === 1 || startDayOfMonth === 16 ? [start] : [];

  return searchNextDatesFrom(start, initial);
};

export const filterOutUndefined = <T>(array: Array<T | undefined>): T[] => array.filter((e) => e !== undefined) as T[];

export const isCashFlowCollection = (
  cashFlow: GroupedCashFlow | CashFlowV2 | CashFlowCollection,
): cashFlow is CashFlowCollection => {
  return (cashFlow as CashFlowCollection).cashFlows !== undefined;
};

export const isAnalysisCashFlow = (cashFlow: GroupedCashFlow | CashFlowV2): cashFlow is GroupedCashFlow => {
  return (cashFlow as GroupedCashFlow).total !== undefined;
};

export const cashFlowHasLabel = (cashFlow: GroupedCashFlow): cashFlow is GroupedCashFlow & { label: string } =>
  !!cashFlow.label && cashFlow.label !== 'unknown';

export const cashFlowIsOfOneOfThoseCategories = (
  cashFlow: { category: TransactionCategory },
  categories: TransactionCategory[],
): boolean => categories.includes(cashFlow.category);

export const cashFlowIsOfOneOfThoseTypes = (cashFlow: { type?: TransactionType }, types: TransactionType[]): boolean =>
  !!cashFlow.type && types.includes(cashFlow.type);
